import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { Notification } from "@rio-cloud/rio-uikit";
import { CheckpointStop, IOnGoingTrip, ITripStopItem } from "@types";

import { ICoordinates, IRoutePoint, ISandboxMarker, getBoundingBox } from "src/components/Map";
import { useDebounce } from "src/hooks/useDebounce";
import { AuthContext } from "src/providers/AuthProvider";
import { ConfigPaginator } from "src/components/PaginatorSize";
import { WebSocketContext } from "src/providers/WebsocketProvider";
import { getCheckpointDetailByIdV2 } from "src/services/checkpoint";
import { fetchDetail, fetchUrltripShare } from "src/services/routes";
import { acceptTrip, fetchDetailsTripOnGoing, fetchTripsListOnGoing, fetchUnplannedStops, finishTripManually, getSuggestedRoute } from "src/services/trips";
import { Result } from "src/types/routes";
import { decode } from "src/utils/flexiblePolyline";
import { config } from '../../../config';
import { mountOnGoingDashboard } from "./mountOnGoingDashboard";
import { Position } from "@rio-cloud/rio-uikit/mapTypes";
import { useHandleRoutes } from "@hooks";
import { getTripIdFromUrl } from "@utils";

const positionDefault = {
  lat: -23.5475,
  lng: -46.63611,
}

const PAGE_SIZE = 10;

const resolveCheckpointId = (checkpointId: unknown): string | undefined => {
  if (!checkpointId) return undefined;
  if (typeof checkpointId === "string") return checkpointId;
  if (typeof checkpointId === "object" && checkpointId !== null && "id" in checkpointId) {
    return (checkpointId as { id?: string }).id;
  }
  return undefined;
};

export const useOnGoingTripsController = () => {
  const pageRef: React.LegacyRef<any> = useRef(null);
  const location = useLocation();
  const [routes, setRoutes] = useState<Array<IOnGoingTrip>>([]);
  const { followers, rioDrivers } = useContext(AuthContext);
  const [searchValue, setSearchValue] = useState("");
  const [sort, setSort] = useState<{ [name: string]: string }>({ "dateStarted": "desc" });
  const [fieldSort, setFieldSort] = useState("dateStarted");
  const [selected, setSelected] = useState<IOnGoingTrip | null>();
  const [details, setDetails] = useState<Result | null>();
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [totalTrips, setTotalTrips] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE);
  const [isPolyline, setIsPolyline] = useState(false);
  const [zoom, setZoom] = useState(10);
  const ctx = useContext(WebSocketContext);
  const { handleGenerateMarker, handleSimpleLocation, handleRoutes } = useHandleRoutes();

  const [dashboard, setDashboard] = useState({
    ongoingRoute: 0,
    outOfRoute: 0,
    overdueRoute: 0,
    earlyRoute: 0,
  });
  const [filterByType, setFilterByType] = useState<string>();

  const [position, setPosition] = useState<ICoordinates | undefined>(positionDefault);
  const [boundingBox, setBoundingBox] = useState<
    { top: number; left: number; right: number; bottom: number } | undefined
  >();
  const [segments, setSegments] = useState<IRoutePoint[]>([]);
  const [markers, setMarkers] = useState<ISandboxMarker[]>([]);
  const [view, setView] = useState<'DEFAULT' | 'DETAILS'>('DEFAULT');
  const [closed, setClosed] = useState(true);
  const [stops, setStops] = useState<ITripStopItem[]>([]);
  const [currencySegments, setCurrencySegments] = useState<IRoutePoint[]>([]);
  const [suggestedSegments, setSuggestedSegments] = useState<IRoutePoint[]>([]);
  const [suggestedRouteData, setSuggestedRouteData] = useState<any>(null);
  const [updatedLenght, setUpdatedLengh] = useState(0);
  const [statusTripNow, setStatusTripNow] = useState('');
  const [statusRouteNow, setStatusRouteNow] = useState('');
  const [progressNow, setProgressNow] = useState<string>();
  const [markerNow, setMarkerNow] = useState<ISandboxMarker>()
  const [locationNow, setLocationNow] = useState('');
  const debouncedSearch = useDebounce(searchValue, 400);
  const [checkpoint, setCheckpoint] = useState<CheckpointStop>();
  const [openStages, setOpenStages] = useState(false);
  const [suggestedRoutes, setSuggestedRoutes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingAcceptTrip, setIsLoadingAcceptTrip] = useState(false);

  // Último trip-id já aberto via deep-link (?trip-id=...). Guarda por VALOR para
  // não reabrir o mesmo id, mas reagir quando a URL aponta para outra viagem.
  const lastOpenedTripIdFromUrlRef = useRef<string | null>(null);

  const handleClearSuggest = async () => {
    setSuggestedRoutes([]);
    setSuggestedRouteData(undefined);
    setSuggestedSegments([]);
  }

  useEffect(() => {
    handleClearSuggest()
  }, [])

  // NÃO REMOVER, WEBSOCKET DESABILITADO.
  // useEffect(() => {
  //   if (ctx?.lastMessage?.data && ctx?.lastMessage?.data.includes('TRIP_LOCATION_UPDATE')) {
  //     setUpdatedLengh(updatedLenght + 1);
  //     fetchAllTrips();
  //   }
  // }, [ctx])
  // useEffect(() => {
  //   if (updatedLenght > 0 && selected) {
  //     Update();
  //   }
  // }, [updatedLenght])

  const fetchSuggestedRoutes = async () => {
    const newSuggestedRoutes: any[] = await Promise.all(routes.map(async (route) => {
      try {
        const suggData = suggestedRoutes.find((f) => f.tripId === route.tripId && f.data);
        if (suggData) {
          return suggData;
        }

        const suggestedRouteToUse = await getSuggestedRoute(route.tripId);
        return {
          tripId: route.tripId,
          data: suggestedRouteToUse.length > 0 ? suggestedRouteToUse : null
        };
      } catch (error) {
        return {
          tripId: route.tripId,
          data: null
        };
      }
    }));
    setSuggestedRoutes(newSuggestedRoutes);
  };

  useEffect(() => {
    fetchSuggestedRoutes();
  }, [routes]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    fetchAllTrips(signal);

    return () => {
      controller.abort();
    };

  }, [debouncedSearch, sort, fieldSort, filterByType, size]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchAllTrips();
    }, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [debouncedSearch, sort, fieldSort, filterByType, size]);


  const loadCheckpointDetails = async (checkpointId: string) => {
    try {
      const response = await getCheckpointDetailByIdV2(checkpointId);
      setCheckpoint(response?.data?.result);
      setClosed(true);
      setOpenStages(true);

    } catch (err) {
      console.error("Erro ao buscar detalhes da viagem:", err);
    }
  };

  const fetchAllTrips = async (signal?: AbortSignal, search = debouncedSearch) => {
    try {
      let all: IOnGoingTrip[] = [];
      let pagesTemp = pages;

      const res = await fetchTripsListOnGoing(1, size, search.length > 0 ? search : "", sort[fieldSort], fieldSort, filterByType, { signal });
      if (res.data.result) {
        all = res.data?.result?.routes || [];
        pagesTemp = res.data?.result?.pages || 0;
        const dashboardData = mountOnGoingDashboard(res.data?.result);
        const totalCount = res.data?.result?.allTrips || 0;

        setRoutes(all);
        setPages(pagesTemp);
        setTotalTrips(totalCount);
        setDashboard(dashboardData)
      }
      setIsLoading(false);
      setIsLoadingMore(false);
    } catch (error) {
      if ((error as any).name !== "CanceledError")
        Notification.error("Não foi possível buscar as rotas.");
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchRouteDetails = async (skipSuggestedRoute = false) => {
    try {
      const routeId = selected?.routeId as string;
      const tripId = selected?.tripId as string;
      const response = await fetchDetail(routeId);

      const plannedStopsDetailed = response.data.result.stops
        .filter((stop: any) => stop.type?.toUpperCase() !== "UNPLANNED")
        .map((stop: any) => ({
          id: stop.id,
          name: stop.stop?.note ?? stop.stopName ?? "Parada programada",
          category: stop.stop?.category,
          order: stop.stopQueueOrder,
          stayTime: (stop.stayTime as string)?.split("T")[1]?.substring(0, 5),
          position: {
            lat: stop.stop?.position?.lat,
            lng: stop.stop?.position?.long,
            label: stop.stop?.position?.address?.label ?? "",
          },
          checkpointId: resolveCheckpointId(stop.checkpointId),
        }));

      const plannedStopsForInputs: ITripStopItem[] = plannedStopsDetailed.map((stop: any) => ({
        note: stop.name,
        label: stop.position.label,
        checkpointId: stop.checkpointId,
        order: stop.order,
        routeStopId: stop.id,
      }));

      let unplannedStopsForInputs: ITripStopItem[] = [];
      let unplannedMarkers: ISandboxMarker[] = [];

      if (routeId && tripId) {
        try {
          const unplannedResponse = await fetchUnplannedStops(routeId, tripId);
          const unplannedRaw = unplannedResponse?.data?.result ?? unplannedResponse?.result ?? [];

          unplannedStopsForInputs = (unplannedRaw || []).map((stop: any, index: number) => {
            const lat = stop.position?.lat;
            const lng = stop.position?.lng;
            const label =
              stop.position?.address?.label ??
              stop.position?.label ??
              stop.stopName ??
              (lat != null && lng != null ? `${lat}, ${lng}` : "Parada não programada");

            return {
              note: `${stop.stopName ?? "Parada"} (Não programada)`,
              label,
              checkpointId: resolveCheckpointId(stop.checkpointId),
              order: stop.stopQueueOrder ?? plannedStopsForInputs.length + index + 1,
            };
          });

          unplannedMarkers = (unplannedRaw || [])
            .filter((stop: any) => stop.position?.lat != null && stop.position?.lng != null)
            .map((stop: any, index: number) => ({
              id: `unplanned-${stop.routeStopId ?? stop.stopId ?? index}`,
              markerProps: {
                iconNames: ["rioglyph rioglyph-map-marker"],
                name: stop.stopName ?? "Parada não programada",
                markerColor: "bg-map-marker-danger",
                active: false,
                fixed: false,
              },
              position: {
                lat: stop.position.lat,
                lng: stop.position.lng,
              },
            }));
        } catch (error) {
          console.error("Erro ao buscar paradas não programadas:", error);
        }
      }

      if (!skipSuggestedRoute) {
        await processSuggestedRouteData(tripId);
      }
      setDetails(response.data.result);
      setStops(
        [...plannedStopsForInputs, ...unplannedStopsForInputs].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        )
      );

      const { zoom, markers: plannedMarkers, position, segments } = handleRoutes(
        response.data.result.responseHere.routes,
        plannedStopsDetailed
      );

      setSegments(segments);
      setPosition(position);
      setMarkers([...plannedMarkers, ...unplannedMarkers]);
      setZoom(zoom);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAcceptTrip = async (tripId: string, accepted: boolean) => {
    setIsLoadingAcceptTrip(true);
    try {
      await acceptTrip({ tripId, accepted });
      await handleClearSuggest();
      await fetchRouteDetails(true); // skipSuggestedRoute = true
      await fetchAllTrips();
      Notification.success("Resposta enviada com sucesso");
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Notification.error(error?.response?.data?.message);
      } else {
        Notification.error("Não foi possível aceitar a viagem.");
      }
    } finally {
      setIsLoadingAcceptTrip(false);
    }
  }

  const processSuggestedRoute = (routes: any[]): IRoutePoint[] => {
    if (!routes || routes.length === 0) {
      return [];
    }

    const route = routes[0];
    const data = route?.sections || [];
    if (data.length === 0) {
      return [];
    }

    const getPolylineArray = data.map((item: any) => {
      try {
        return {
          shape: decode(item.polyline),
        };
      } catch (error) {
        return { shape: { polyline: [] } };
      }
    });

    let ArrayOfShape: any[] = [];
    getPolylineArray.forEach((item: any) => {
      if (item.shape?.polyline && Array.isArray(item.shape.polyline)) {
        ArrayOfShape.push(item.shape.polyline);
      }
    });

    if (ArrayOfShape.length === 0) {
      return [];
    }

    const concatArrays = ArrayOfShape.flat();
    const getPoints = concatArrays.map((value) => {
      if (Array.isArray(value) && value.length >= 2) {
        return {
          lat: value[0].toString(),
          lng: value[1].toString(),
        };
      }
      return null;
    }).filter((point): point is IRoutePoint => point !== null);

    return getPoints;
  };

  const getLastSuggestion = (suggestedRouteData: any) => {
    if (Array.isArray(suggestedRouteData) && suggestedRouteData.length > 0) {
      return suggestedRouteData[suggestedRouteData.length - 1];
    }

    if (suggestedRouteData?.result && suggestedRouteData.result.length > 0) {
      return suggestedRouteData.result[suggestedRouteData.result.length - 1];
    }

    return null;
  };

  const processRouteSegments = (routes: any[]) => {
    const suggestedRoutePoints = processSuggestedRoute(routes);
    setSuggestedSegments(suggestedRoutePoints);

    if (suggestedRoutePoints.length > 0) {
      const values = getBoundingBox(suggestedRoutePoints);
      const paddedValues = {
        top: values.top + 0.1,
        bottom: values.bottom - 0.02,
        left: values.left - 0.1,
        right: values.right + 0.1,
      };
      setBoundingBox(paddedValues);
    }
  };

  const processValidSuggestion = (lastSuggestion: any, tripId: string) => {
    const suggestedDataWithTripId = {
      ...lastSuggestion,
      tripId: tripId
    };
    setSuggestedRouteData(suggestedDataWithTripId);

    const routes = lastSuggestion.completed_route?.routes;
    if (routes && routes.length > 0) {
      processRouteSegments(routes);
    }
  };

  const processSuggestedRouteData = async (tripId: string) => {
    try {
      // isso aqui ta causando problemas de assincronismo, quando o handleClearSuggest é chamado no mesmo momento
      // em que esse método processSuggestedRouteData está em execução, o suggestedRoutes que foi limpo no handle
      // clear suggest é setado novamente com os valores antigos. Isso faz aparecer o modal de Sugestão de rota
      // novamente mesmo após a sugestão de rota ser aprovada/rejeitada
      //
      // [Removido]
      // let suggestedRouteToUse: any = suggestedRoutes.find((f: any) => f.tripId === tripId && f.data)?.data;

      // Então por consistência, sempre consultamos a sugestão de rota no backend, já que ele mantém o estado real
      // da rota e suas sugestões
      let suggestedRouteToUse: any = await getSuggestedRoute(tripId);

      if (!suggestedRouteToUse) {
        handleClearSuggest();
        return;
      }

      const lastSuggestion = getLastSuggestion(suggestedRouteToUse);

      if (lastSuggestion) {
        processValidSuggestion(lastSuggestion, tripId);
      } else {
        handleClearSuggest();
      }

    } catch (error) {
      console.error('[processSuggestedRouteData]:: ERROR - failed to process suggested route:', error);
    }
  };

  const handleClear = () => {
    setSelected(null);
    setDetails(null);
    setMarkers([]);
    setSegments([]);
    setCurrencySegments([]);
    setSuggestedSegments([]);
    setSuggestedRouteData(null);
    setPosition(positionDefault);
    setIsPolyline(false);
  }

  const handleSelectFilterByType = (type: string) => {
    setPage(1);
    setSize(PAGE_SIZE);
    handleClear();
    setFilterByType(prev => prev === type ? '' : type);
  }

  const send = (positions: Position[]) => {
    const lastLocation = positions[positions.length - 1];
    if (lastLocation) {
      setLocationNow(`${handleSimpleLocation(lastLocation?.lat)},${handleSimpleLocation(lastLocation.lng)}`);
      setMarkerNow(handleGenerateMarker({
        lat: lastLocation?.lat,
        lng: lastLocation?.lng,
        mode: 'street_truck',
      }));
      setPosition({
        lat: lastLocation?.lat,
        lng: lastLocation?.lng,
      })
    }
    setCurrencySegments(positions);
  }

  const handleDetails = (select: IOnGoingTrip) => {
    setSelected(select);
    ctx?.sendMessage(select.tripId);
    ctx?.handleSaveTrip(select.tripId);
    setView('DETAILS');
    setClosed(false);
  }

  // Abre automaticamente os detalhes da viagem quando a tela de monitoramento é
  // acessada via deep-link com `trip-id` na URL
  // (ex.: #/routing/map/trips?trip-id=<uuid>). Espelha o fluxo de abertura por
  // notificação, mas a origem do tripId é a query string (dentro do hash).
  // Reage a `location.search` para também abrir quando o host troca o trip-id.
  useEffect(() => {
    if (isLoading) return;

    const tripIdFromUrl = getTripIdFromUrl();
    if (!tripIdFromUrl || lastOpenedTripIdFromUrlRef.current === tripIdFromUrl) return;

    lastOpenedTripIdFromUrlRef.current = tripIdFromUrl;

    const openTripFromUrl = async () => {
      const existingTrip = routes.find((r) => r.tripId === tripIdFromUrl);
      if (existingTrip) {
        handleDetails(existingTrip);
        return;
      }

      try {
        const response = await fetchDetailsTripOnGoing(tripIdFromUrl);
        if (response.data?.result) {
          handleDetails(response.data.result as IOnGoingTrip);
        }
      } catch (error) {
        Notification.error("Não foi possível abrir os detalhes da viagem.");
      }
    };

    openTripFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, location.search]);

  const handleShare = async (id: string) => {
    try {
      // if (!isPolyline || !locationNow) return;
      const response = await fetchUrltripShare(id);
      window.open(`${config.publicRoute}?link=${response.data.result}`, '_blank');
    } catch (error) {

    }
  }

  const Update = async () => {
    const response = await fetchDetailsTripOnGoing(selected?.tripId);

    if (response.data.result) {
      setStatusRouteNow(response.data.result.routeStatusNow);
      setStatusTripNow(response.data.result.tripStatusNow);
      setProgressNow(response.data.result.actualProgress);
      setIsPolyline(false);
      setSelected(response.data.result);

      if (response.data.result.positions?.length) {
        send(response.data.result.positions?.reverse())
      }
    }
  }

  useEffect(() => {
    if (selected?.routeId) {
      fetchRouteDetails();
      Update();

      const intervalId = setInterval(() => {
        if (!isLoadingAcceptTrip) {
          // só pra evitar consultar o back enquanto a ação de aceitar/rejeitar sugestão está sendo executada.
          Update();
          processSuggestedRouteData(selected?.tripId as string);
        }
      }, 30000);

      return () => clearInterval(intervalId);
    }
  }, [selected?.routeId])

  const handleSort = (dir: string, field: string) => {
    setSort({ [field]: dir });
    setFieldSort(field);
  };

  const handlePage = async (config: ConfigPaginator) => {
    setIsLoadingMore(true);
    setSize(config.next);
  };

  const handleResetPage = () => {
    setSize(PAGE_SIZE);
  };

  const handleSearchValueChange = (text: string) => {
    setSearchValue(text);
  };

  const handleFinishTrip = async (tripId: string) => {
    try {
      await finishTripManually(tripId);
      Notification.success("Viagem finalizada com sucesso");
      handleCloseSidebar();
      await fetchAllTrips();
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Notification.error(error.response.data.message);
      } else {
        Notification.error("Não foi possível finalizar a viagem.");
      }
    }
  };

  const handleCloseSidebar = () => {
    setClosed(true);
    setView('DEFAULT');
    fetchAllTrips();
  };

  return {
    pageRef,
    routes,
    followers,
    searchValue,
    sort,
    fieldSort,
    selected,
    details,
    page,
    pages,
    isPolyline,
    dashboard,
    filterByType,
    position,
    boundingBox,
    segments,
    markers,
    view,
    closed,
    stops,
    currencySegments,
    suggestedSegments,
    suggestedRouteData,
    statusTripNow,
    statusRouteNow,
    progressNow,
    markerNow,
    locationNow,
    checkpoint,
    openStages,
    size,
    totalTrips,
    handleSelectFilterByType,
    handleDetails,
    handleShare,
    handleSort,
    handlePage,
    handleSearchValueChange,
    handleCloseSidebar,
    setSearchValue,
    loadCheckpointDetails,
    setOpenStages,
    setClosed,
    fetchRouteDetails,
    handleAcceptTrip,
    handleFinishTrip,
    handleResetPage,
    PAGE_SIZE,
    suggestedRoutes,
    rioDrivers,
    isLoading,
    isLoadingMore,
    isLoadingAcceptTrip,
    zoom,
  };
}; 